import React, {useEffect, useMemo, useState} from "react"
import {useMutation, useQuery} from "@apollo/client"
import {FileSearch} from "components/backoffice/Storage/File/FileSearch"
import providerListQuery from "graphql/Storage/Provider/query/providerList"
import revisionListQuery from "graphql/Media/Revision/query/mediaRevisionList"
import prepareMutation from "graphql/Media/Revision/mutation/mediaUpgradePrepare"
import confirmMutation from "graphql/Media/Revision/mutation/mediaUpgradeConfirm"
import cancelMutation from "graphql/Media/Revision/mutation/mediaUpgradeCancel"
import activateMutation from "graphql/Media/Revision/mutation/mediaRevisionActivate"
import retryMutation from "graphql/Media/Revision/mutation/mediaUpgradeRetry"
import sourceAssetListQuery from "graphql/Storage/Ingestion/query/sourceAssetList"

const bytes = (value: number) => {
  if (!value) return "—"
  const units = ["o", "Ko", "Mo", "Go", "To"]
  const exponent = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1)
  return `${(value / Math.pow(1024, exponent)).toFixed(exponent > 2 ? 1 : 0)} ${units[exponent]}`
}

const resolution = (video: any) => video?.width && video?.height ? `${video.width}×${video.height}` : "Inconnue"
const formatVideo = (video: any) => [resolution(video), video?.codec, video?.hdr === true ? "HDR" : video?.hdr === false ? "SDR" : null].filter(Boolean).join(" · ")

export const MediaUpgrade = ({mediaId}: {mediaId: string}) => {
  const [open, setOpen] = useState(false)
  const [sourceFile, setSourceFile] = useState<any>(null)
  const [providerId, setProviderId] = useState("")
  const [draft, setDraft] = useState<any>(null)
  const [allowRegression, setAllowRegression] = useState(false)
  const [error, setError] = useState("")

  const providers = useQuery(providerListQuery)
  const inboxAssets = useQuery(sourceAssetListQuery, {fetchPolicy: "cache-and-network"})
  const revisions = useQuery(revisionListQuery, {
    variables: {mediaId},
    pollInterval: 5000,
    fetchPolicy: "no-cache",
  })
  const allProviders = providers.data?.providerList || []
  const mediaProviders = allProviders.filter((provider: any) => provider.target === "MEDIA")
  const providerList = mediaProviders.length > 0 ? mediaProviders : allProviders
  const eligibleAssets = (inboxAssets.data?.sourceAssetList || []).filter((asset: any) => asset.status === "NEEDS_REVIEW")
  const selectedProviderId = providerId || providerList[0]?.id || ""
  const [prepare, prepareState] = useMutation(prepareMutation)
  const [confirm, confirmState] = useMutation(confirmMutation)
  const [cancel] = useMutation(cancelMutation)
  const [activate] = useMutation(activateMutation)
  const [retry] = useMutation(retryMutation)
  const comparison = draft?.comparison
  const regressions = comparison?.regressions || []
  const busy = prepareState.loading || confirmState.loading

  const currentRevisionId = useMemo(() => revisions.data?.revisions?.find((revision: any) => revision.status === "PUBLISHED")?.id, [revisions.data])

  useEffect(() => {
    if (!draft?.id) return
    const liveRevision = revisions.data?.revisions?.find((revision: any) => revision.id === draft.id)
    if (liveRevision && liveRevision.status !== draft.status) setDraft(liveRevision)
  }, [draft?.id, draft?.status, revisions.data])

  const analyze = async () => {
    setError("")
    try {
      const response = await prepare({variables: {
        mediaId,
        sourceFileId: sourceFile?.id,
        destinationProviderId: selectedProviderId,
      }})
      setDraft(response.data?.revision)
      await revisions.refetch()
    } catch (exception) {
      setError(exception?.message || String(exception))
    }
  }

  const confirmUpgrade = async () => {
    setError("")
    try {
      const response = await confirm({variables: {revisionId: draft.id, allowRegression}})
      setDraft(response.data?.revision)
      await revisions.refetch()
    } catch (exception) {
      setError(exception?.message || String(exception))
    }
  }

  const cancelUpgrade = async () => {
    await cancel({variables: {revisionId: draft.id}})
    setDraft(null)
    setOpen(false)
    await revisions.refetch()
  }

  const rollback = async (revisionId: string) => {
    setError("")
    try {
      await activate({variables: {revisionId}})
      await revisions.refetch()
    } catch (exception) {
      setError(exception?.message || String(exception))
    }
  }

  const retryUpgrade = async (revisionId: string) => {
    setError("")
    try {
      await retry({variables: {revisionId}})
      await revisions.refetch()
    } catch (exception) {
      setError(exception?.message || String(exception))
    }
  }

  return <section className="media-upgrade-container">
    <h2>Révisions</h2>
    <button className="btn btn-primary" onClick={() => setOpen(!open)}>Améliorer la qualité</button>

    {open && <div className="card mt-3">
      <div className="card-body">
        <h3>Nouvelle source</h3>
        {!draft && <>
          {eligibleAssets.length > 0 && <>
            <label>Fichier détecté dans l’INBOX</label>
            <select className="form-control mb-2" value={eligibleAssets.find((asset: any) => asset.fileId === sourceFile?.id)?.id || ""} onChange={event => {
              const asset = eligibleAssets.find((candidate: any) => candidate.id === event.target.value)
              setSourceFile(asset ? {id: asset.fileId, name: asset.name} : null)
            }}>
              <option value="">Sélectionner un fichier INBOX…</option>
              {eligibleAssets.map((asset: any) => <option key={asset.id} value={asset.id}>{asset.name} · {bytes(asset.size)}</option>)}
            </select>
          </>}
          <label>Ou rechercher un fichier connu de ZIPFLIX</label>
          <FileSearch value={sourceFile} onSelect={setSourceFile} />
          <label className="mt-2">Stockage de destination</label>
          <select className="form-control" value={selectedProviderId} onChange={event => setProviderId(event.target.value)}>
            {providerList.map((provider: any) => <option key={provider.id} value={provider.id}>{provider.name} · {provider.type}</option>)}
          </select>
          <button className="btn btn-info mt-3" disabled={!sourceFile?.id || !selectedProviderId || busy} onClick={analyze}>
            {prepareState.loading ? "Analyse en cours…" : "Analyser et comparer"}
          </button>
        </>}

        {draft?.status === "WAITING_CONFIRMATION" && comparison && <div className="mt-3">
          <h3>{comparison.verdict === "PROBABLE_UPGRADE" ? "Amélioration probable" : comparison.verdict === "REGRESSION_WARNING" ? "Régressions détectées" : "Comparaison incertaine"}</h3>
          <table className="table table-bordered">
            <thead><tr><th>Critère</th><th>Actuel</th><th>Nouveau master</th></tr></thead>
            <tbody>
              <tr><td>Vidéo</td><td>{formatVideo(comparison.current?.video)}</td><td>{formatVideo(comparison.candidate?.video)}</td></tr>
              <tr><td>Durée</td><td>{Math.round(comparison.current?.duration || 0)} s</td><td>{Math.round(comparison.candidate?.duration || 0)} s</td></tr>
              <tr><td>Audio</td><td>{comparison.current?.audioLanguages?.join(", ") || "Inconnu"}</td><td>{comparison.candidate?.audio?.map((audio: any) => `${audio.language} ${audio.codec}`).join(", ")}</td></tr>
              <tr><td>Stockage final estimé</td><td>{bytes(comparison.current?.playbackBytes)}</td><td>{bytes(comparison.estimatedFinalBytes)}</td></tr>
              <tr><td>Pic temporaire estimé</td><td colSpan={2}>{bytes(comparison.estimatedPeakBytes)}</td></tr>
            </tbody>
          </table>
          {comparison.improvements?.length > 0 && <div className="alert alert-success">Améliorations : {comparison.improvements.join(", ")}</div>}
          {regressions.length > 0 && <div className="alert alert-danger">Régressions : {regressions.join(", ")}</div>}
          {comparison.warnings?.length > 0 && <div className="alert alert-warning">Points à vérifier : {comparison.warnings.join(", ")}</div>}
          {regressions.length > 0 && <label><input type="checkbox" checked={allowRegression} onChange={event => setAllowRegression(event.target.checked)} /> Je confirme malgré les régressions</label>}
          <div className="mt-3">
            <button className="btn btn-success mr-2" disabled={busy || (regressions.length > 0 && !allowRegression)} onClick={confirmUpgrade}>Confirmer l’upgrade</button>
            <button className="btn btn-secondary" onClick={cancelUpgrade}>Annuler</button>
          </div>
        </div>}

        {draft && ["TRANSCODING", "VALIDATING"].includes(draft.status) && <div className="alert alert-info mt-3">La nouvelle révision est traitée en arrière-plan. Le média actuel reste disponible.</div>}
        {draft?.status === "PUBLISHED" && <div className="alert alert-success mt-3">La nouvelle révision est publiée. L’ancienne reste disponible pendant 30 jours.</div>}
        {draft?.status === "FAILED" && <div className="alert alert-danger mt-3">{draft.error || "L’upgrade a échoué."}</div>}
      </div>
    </div>}

    {error && <div className="alert alert-danger mt-3">{error}</div>}

    <div className="mt-3">
      {revisions.data?.revisions?.map((revision: any) => <div className="card mb-2" key={revision.id}>
        <div className="card-body">
          <strong>{revision.status === "PUBLISHED" ? "Active" : revision.status}</strong>
          {revision.sourceMetadata?.video && <span> · {formatVideo(revision.sourceMetadata.video)}</span>}
          {revision.creationDate && <span> · {new Date(revision.creationDate * 1000).toLocaleString()}</span>}
          {revision.retentionUntil && <div>Conservée jusqu’au {new Date(revision.retentionUntil * 1000).toLocaleString()}</div>}
          {revision.status === "ARCHIVED" && revision.id !== currentRevisionId && <button className="btn btn-outline-warning btn-sm mt-2" onClick={() => rollback(revision.id)}>Réactiver cette révision</button>}
          {revision.status === "FAILED" && <button className="btn btn-outline-info btn-sm mt-2" onClick={() => retryUpgrade(revision.id)}>Réessayer</button>}
          {revision.error && <div className="text-danger">{revision.error}</div>}
        </div>
      </div>)}
    </div>
  </section>
}
